import React, { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import Topbar from '../components/Topbar';
import ConnectionStatus from '../components/ConnectionStatus';
import axios, { baseURL } from '../libs/axios';

export default function Dashboard({ token, onLogout }) {
  const [me, setMe] = useState(null);
  const [error, setError] = useState('');
  const [datev, setDatev] = useState(null);
  const [jobs, setJobs] = useState([]);
  const [executing, setExecuting] = useState({});
  const [clients, setClients] = useState([]);
  const [clientsLoading, setClientsLoading] = useState(false);
  const [selectedClient, setSelectedClient] = useState(null);
  const [connectionActive, setConnectionActive] = useState(true);
  const [userInfo, setUserInfo] = useState(null);
  const [userInfoLoading, setUserInfoLoading] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const fetchMe = async () => {
      if (!token) return;
      try {
        const res = await axios.get('/auth/me');
        setMe(res.data);
      } catch (e) {
        setError(e?.response?.data?.detail || e.message);
      }
    };
    fetchMe();
  }, [token]);

  useEffect(() => {
    if (!token) return;
    const fetchStatus = async () => {
      try {
        const res = await axios.get('/datev/status');
        setDatev(res.data);
      } catch (_e) {}
    };
    const fetchJobs = async () => {
      try {
        const res = await axios.get('/jobs');
        setJobs(res.data.jobs || []);
      } catch (_e) {}
    };
    fetchStatus();
    fetchJobs();

    const params = new URLSearchParams(location.search);
    if (params.get('datev') === 'connected') {
      window.history.replaceState({}, document.title, location.pathname);
      fetchStatus();
    }
  }, [token, location.search, location.pathname]);

  // Real-time connection monitoring
  useEffect(() => {
    if (!token || !datev?.connected) return;
    
    const monitorConnection = async () => {
      try {
        const res = await axios.get('/datev/status');
        setDatev(res.data);
        setConnectionActive(true);
      } catch (e) {
        setConnectionActive(false);
        console.warn('Connection monitoring failed:', e.message);
      }
    };

    // Check connection status every 2 minutes
    const interval = setInterval(monitorConnection, 120000);
    return () => clearInterval(interval);
  }, [token, datev?.connected]);

  useEffect(() => {
    if (!token) return;
    if (datev && datev.connected) {
      (async () => {
        try {
          setClientsLoading(true);
          const res = await axios.get('/datev/clients');
          const clientsList = res.data.clients || [];
          setClients(clientsList);
          
          // Restore selected client from localStorage if available
          try {
            const storedClientStr = localStorage.getItem('selectedDatevClient');
            if (storedClientStr) {
              const storedClient = JSON.parse(storedClientStr);
              // Verify the stored client exists in the current clients list
              const matchingClient = clientsList.find(c => c.id === storedClient.id);
              if (matchingClient) {
                setSelectedClient(matchingClient);
              }
            }
          } catch (e) {
            console.warn('Failed to restore selected client from localStorage:', e);
          }
        } catch (_e) {
          // ignore
        } finally {
          setClientsLoading(false);
        }
      })();

      // Fetch user information
      (async () => {
        try {
          setUserInfoLoading(true);
          const res = await axios.get('/datev/user-info');
          setUserInfo(res.data);
        } catch (_e) {
          // ignore user info fetch errors
        } finally {
          setUserInfoLoading(false);
        }
      })();
    }
  }, [token, datev]);

  const selectClient = (client) => {
    if (!client) return;
    setSelectedClient(client);

    // Save selected client to local storage
    try {
      localStorage.setItem('selectedDatevClient', JSON.stringify(client));
    } catch (e) {
      console.warn('Failed to save client to local storage:', e);
    }
  };

  const connectDatev = async (type) => {
    try {
      const url = `${baseURL}/auth/datev/login/dev?token_type=${encodeURIComponent(type)}&token=${encodeURIComponent(token || '')}`;
      window.location.href = url;
    } catch (e) {
      setError(e.message);
    }
  };

  const disconnectDatev = async () => {
    try {
      await axios.post('/datev/disconnect');
      setDatev({ connected: false });
    } catch (_e) {}
  };

  const executeJob = async (id) => {
    setExecuting((p) => ({ ...p, [id]: true }));
    try {
      await axios.post(`/jobs/${id}/process`);
      const res = await axios.get('/jobs');
      setJobs(res.data.jobs || []);
    } catch (_e) {}
    finally {
      setExecuting((p) => ({ ...p, [id]: false }));
    }
  };

  if (!token) {
    return null; // Landing page is handled by route
  }

  return (
    <div className="page">
      <Topbar onLogout={onLogout} />
      <main className="content">
        <h2>Dashboard</h2>
        {error && <div className="alert error">{error}</div>}
        {me && (
          <div className="card">
            <div className="muted">Logged in as</div>
            <div className="strong">{me.email}</div>
          </div>
        )}

        <section className="stack">
          <h3>DATEV Connection</h3>
          
          <ConnectionStatus
            connected={datev?.connected || false}
            tokenType={datev?.token_type}
            expiresAt={datev?.expires_at}
            environment="sandbox" // TODO: Make this dynamic based on user preference
            isActive={connectionActive}
            userInfo={userInfo}
          />

          {!datev?.connected ? (
            <div className="stack mt-4">
              <div className="muted">Connect your DATEV account:</div>
              <div className="stack" style={{ gridTemplateColumns: '1fr 1fr', display: 'grid', gap: 12 }}>
                <button className="btn-primary" onClick={() => connectDatev('short')}>Connect with DATEV (Short-term)</button>
                <button className="btn-secondary" onClick={() => connectDatev('long')}>Connect with DATEV (Long-term)</button>
              </div>
            </div>
          ) : (
            <div className="mt-4 flex gap-2">
              <button className="btn-outline" onClick={disconnectDatev}>Disconnect DATEV</button>
              <Link to="/logs" className="btn-secondary">View API Logs</Link>
            </div>
          )}
        </section>

        <section className="stack">
          <h3>DATEV Clients</h3>
          {!datev?.connected ? (
            <div className="muted">Connect your DATEV account to load clients.</div>
          ) : (
            <div className="card">
              {clientsLoading ? (
                <div className="muted">Loading clients…</div>
              ) : clients.length === 0 ? (
                <div className="muted">No clients available.</div>
              ) : (
                <div className="stack">
                  <div className="muted">Select an active client for uploads:</div>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr', gap: 8 }}>
                    {clients.map((c) => (
                      <button 
                        key={c.id} 
                        className={selectedClient?.id === c.id ? 'btn-primary' : 'btn-outline'} 
                        style={{ justifyContent: 'flex-start' }} 
                        onClick={() => selectClient(c)}
                      >
                        <span className="strong" style={{ marginRight: 8 }}>{c.name}</span>
                        <span className="muted">{c.advisor_number || ''}{c.client_number ? ` • ${c.client_number}` : ''}</span>
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </section>

        <section className="stack">
          <h3>Jobs</h3>
          {jobs.length === 0 ? (
            <div className="muted">No jobs yet.</div>
          ) : (
            <div className="stack">
              {jobs.map((j) => (
                <div className="card" key={j.id}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                    <div style={{ flex: 1 }}>
                      <div className="strong">{(j.type && typeof j.type === 'string' ? j.type.toUpperCase() : 'JOB')} • {j.status}</div>
                      <div className="muted">Created {new Date(j.created_at || j.createdAt || Date.now()).toLocaleString()}</div>
                      {j.files && <div style={{ fontSize: 12, color: '#64748b' }}>{j.files.length} file(s)</div>}
                    </div>
                    <button className="btn-primary" disabled={executing[j.id] || j.status === 'processing'} onClick={() => executeJob(j.id)}>
                      {executing[j.id] ? 'Processing...' : 'Process job'}
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </section>
      </main>
    </div>
  );
}
